import React, { Suspense, lazy } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { AppShell } from '@/shell/AppShell'
import { LoadingSkeleton } from '@/components/feedback/LoadingSkeleton'
import { ErrorPanel } from '@/components/feedback/ErrorPanel'
import { DEFAULT_MODULE } from '@/lib/constants'
import { storageGet, storageSet } from '@/lib/storage'
import { LAST_MODULE_KEY } from '@/lib/constants'
import LandingPage from '@/app/LandingPage'
import { normalizeDashboardPath } from '@/lib/routes'
import { useQuietHours } from '@/lib/quiet-hours'

// Lazy-load module routes
const KuroBoardOverview = lazy(() => import('@/modules/kuroboard/views/KuroBoardOverview'))
const KuroBoardProjects = lazy(() => import('@/modules/kuroboard/views/KuroBoardProjects'))
const KuroBoardProjectDetail = lazy(() => import('@/modules/kuroboard/views/KuroBoardProjectDetail'))
const KuroBoardNotifications = lazy(() => import('@/modules/kuroboard/views/KuroBoardNotifications'))

const KuroTraderOverview = lazy(() => import('@/modules/kurotrader/views/KuroTraderOverview'))
const KuroTraderPerformance = lazy(() => import('@/modules/kurotrader/views/KuroTraderPerformance'))
const KuroTraderPortfolio = lazy(() => import('@/modules/kurotrader/views/KuroTraderPortfolio'))
const KuroTraderSignals = lazy(() => import('@/modules/kurotrader/views/KuroTraderSignals'))

const GokuHealthOverview = lazy(() => import('@/modules/goku-health/views/GokuHealthOverview'))
const GokuHealthServices = lazy(() => import('@/modules/goku-health/views/GokuHealthServices'))
const GokuHealthResources = lazy(() => import('@/modules/goku-health/views/GokuHealthResources'))
const GokuHealthIncidents = lazy(() => import('@/modules/goku-health/views/GokuHealthIncidents'))

const KuroSiteOverview = lazy(() => import('@/modules/kuro-site/views/KuroSiteOverview'))
const KuroSiteNow = lazy(() => import('@/modules/kuro-site/views/KuroSiteNow'))
const KuroSitePosts = lazy(() => import('@/modules/kuro-site/views/KuroSitePosts'))
const KuroSitePostDetail = lazy(() => import('@/modules/kuro-site/views/KuroSitePostDetail'))

const KuroOverview = lazy(() => import('@/modules/kuro/views/KuroOverview'))
const KuroAbout = lazy(() => import('@/modules/kuro/views/KuroAbout'))
const KuroActivity = lazy(() => import('@/modules/kuro/views/KuroActivity'))

const KuroRunnerOverview = lazy(() => import('@/modules/kurorunner/components/KuroRunnerOverview'))
const JobDetail = lazy(() => import('@/modules/kurorunner/components/JobDetail'))

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingSkeleton lines={6} className="mt-4" />}>
      {children}
    </Suspense>
  )
}

function RootRedirect() {
  const last = storageGet<string>(LAST_MODULE_KEY)
  const target = normalizeDashboardPath(last ?? DEFAULT_MODULE)
  return <Navigate to={target} replace />
}

function LegacyModuleRedirect() {
  const location = useLocation()
  const quietHours = useQuietHours()

  if (quietHours) {
    return <Navigate to="/" replace />
  }

  return <Navigate to={normalizeDashboardPath(location.pathname)} replace />
}

function DashboardAvailabilityGate() {
  const quietHours = useQuietHours()

  if (quietHours) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

function NotFound() {
  return (
    <ErrorPanel
      title="Page not found"
      message="This route doesn't exist. Use the navigation to find what you're looking for."
    />
  )
}

// Persist last-visited module
function ModuleEntry({ path, children }: { path: string; children: React.ReactNode }) {
  React.useEffect(() => {
    storageSet(LAST_MODULE_KEY, path)
  }, [path])
  return <>{children}</>
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'dashboard',
        element: <DashboardAvailabilityGate />,
        children: [
          { index: true, element: <RootRedirect /> },
          {
            path: 'kuroboard',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/kuroboard"><KuroBoardOverview /></ModuleEntry></SuspenseWrapper> },
              { path: 'projects', element: <SuspenseWrapper><KuroBoardProjects /></SuspenseWrapper> },
              { path: 'projects/:id', element: <SuspenseWrapper><KuroBoardProjectDetail /></SuspenseWrapper> },
              { path: 'notifications', element: <SuspenseWrapper><KuroBoardNotifications /></SuspenseWrapper> },
            ],
          },
          {
            path: 'kurotrader',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/kurotrader"><KuroTraderOverview /></ModuleEntry></SuspenseWrapper> },
              { path: 'performance', element: <SuspenseWrapper><KuroTraderPerformance /></SuspenseWrapper> },
              { path: 'portfolio', element: <SuspenseWrapper><KuroTraderPortfolio /></SuspenseWrapper> },
              { path: 'signals', element: <SuspenseWrapper><KuroTraderSignals /></SuspenseWrapper> },
            ],
          },
          {
            path: 'goku-health',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/goku-health"><GokuHealthOverview /></ModuleEntry></SuspenseWrapper> },
              { path: 'services', element: <SuspenseWrapper><GokuHealthServices /></SuspenseWrapper> },
              { path: 'resources', element: <SuspenseWrapper><GokuHealthResources /></SuspenseWrapper> },
              { path: 'incidents', element: <SuspenseWrapper><GokuHealthIncidents /></SuspenseWrapper> },
            ],
          },
          {
            path: 'kuro-site',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/kuro-site"><KuroSiteOverview /></ModuleEntry></SuspenseWrapper> },
              { path: 'now', element: <SuspenseWrapper><KuroSiteNow /></SuspenseWrapper> },
              { path: 'posts', element: <SuspenseWrapper><KuroSitePosts /></SuspenseWrapper> },
              { path: 'posts/:slug', element: <SuspenseWrapper><KuroSitePostDetail /></SuspenseWrapper> },
            ],
          },
          {
            path: 'kuro',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/kuro"><KuroOverview /></ModuleEntry></SuspenseWrapper> },
              { path: 'about', element: <SuspenseWrapper><KuroAbout /></SuspenseWrapper> },
              { path: 'activity', element: <SuspenseWrapper><KuroActivity /></SuspenseWrapper> },
            ],
          },
          {
            path: 'kurorunner',
            children: [
              { index: true, element: <SuspenseWrapper><ModuleEntry path="/dashboard/kurorunner"><KuroRunnerOverview /></ModuleEntry></SuspenseWrapper> },
              { path: ':jobId', element: <SuspenseWrapper><JobDetail /></SuspenseWrapper> },
            ],
          },
        ],
      },
      { path: 'kuroboard/*', element: <LegacyModuleRedirect /> },
      { path: 'kurotrader/*', element: <LegacyModuleRedirect /> },
      { path: 'goku-health/*', element: <LegacyModuleRedirect /> },
      { path: 'kuro-site/*', element: <LegacyModuleRedirect /> },
      { path: 'kuro/*', element: <LegacyModuleRedirect /> },
      { path: 'kurorunner/*', element: <LegacyModuleRedirect /> },

      // Catch-all
      { path: '*', element: <NotFound /> },
    ],
  },
])
